import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
  Stack,
} from '@mui/material';
import { ArrowBack, Email, SupportAgent } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import TeksTimeBrandMark from '../components/TeksTimeBrandMark';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);
  const [qaOtp, setQaOtp] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'otp' | 'supervisor'>('email');
  const navigate = useNavigate();

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }
    setLoading(true);
    setMessage(null);
    setQaOtp(null);
    try {
      const response = await apiClient.password.forgotPassword(email);
      setMessage({ type: 'success', text: response.data.message });
      setRequestId(response.data.data?.requestId || null);
      setMaskedEmail(response.data.data?.maskedEmail || null);
      const localOtp = response.data.data?.qaOtp || null;
      if (localOtp) {
        setQaOtp(localOtp);
        setCode(localOtp);
      }
      if (response.data.data?.requestId) {
        setStep('otp');
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to process recovery request.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requestId || !code) {
      setMessage({ type: 'error', text: 'Enter the recovery code from your email.' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const response = await apiClient.password.verifyEmailOtp(requestId, code);
      const recoveryToken = response.data.data?.recoveryToken;
      if (recoveryToken) {
        navigate(`/reset-password?recoveryToken=${encodeURIComponent(recoveryToken)}`);
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'The code is invalid or has expired.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSupervisorRequest = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await apiClient.password.requestSupervisorAssistance(email);
      setMessage({ type: 'info', text: response.data.message });
      setRequestId(response.data.data?.requestId || null);
      setStep('supervisor');
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Unable to request assistance.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySupervisor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requestId || !code) return;
    setLoading(true);
    try {
      const response = await apiClient.password.verifySupervisorCode(requestId, code);
      const recoveryToken = response.data.data?.recoveryToken;
      if (recoveryToken) {
        navigate(`/reset-password?recoveryToken=${encodeURIComponent(recoveryToken)}`);
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'The code is invalid or has expired.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <TeksTimeBrandMark />
          <Typography component="h2" variant="h5" gutterBottom align="center">
            Forgot Password
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Recover your account with an email code, or request Team Lead / Supervisor assistance.
          </Typography>

          {message && (
            <Alert severity={message.type === 'info' ? 'info' : message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}

          {step === 'email' && (
            <Box component="form" onSubmit={handleRequestOtp}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoFocus
              />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Email />}>
                {loading ? 'Sending...' : 'Send Email Code'}
              </Button>
              <Button fullWidth sx={{ mt: 1 }} disabled={loading || !email} startIcon={<SupportAgent />}
                onClick={handleSupervisorRequest}>
                Request Team Lead / Supervisor Assistance
              </Button>
            </Box>
          )}

          {step === 'otp' && (
            <Box component="form" onSubmit={handleVerifyOtp}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter the code sent to {maskedEmail || 'your email'}.
              </Typography>
              {qaOtp && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Local/QA mode: use code <strong>{qaOtp}</strong> (email delivery may be skipped).
                </Alert>
              )}
              <TextField
                margin="normal"
                required
                fullWidth
                label="Recovery Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={loading}
                autoFocus
              />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading}>
                Verify Code
              </Button>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button fullWidth onClick={() => { setStep('email'); setCode(''); setQaOtp(null); }}>Back</Button>
                <Button fullWidth onClick={handleRequestOtp} disabled={loading}>Send New Code</Button>
              </Stack>
            </Box>
          )}

          {step === 'supervisor' && (
            <Box component="form" onSubmit={handleVerifySupervisor}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Assistance requested. Enter the one-time code provided by your Team Lead or Supervisor.
              </Typography>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Supervisor Recovery Code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={loading}
                autoFocus
              />
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading}>
                Verify Code
              </Button>
              <Button fullWidth sx={{ mt: 1 }} onClick={() => { setStep('email'); setCode(''); }}>
                Back
              </Button>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Link component="button" variant="body2" onClick={() => navigate('/login')}>
              <ArrowBack fontSize="small" /> Back to Login
            </Link>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPasswordPage;
